import {Button, DropdownMenu, IconButton} from '@wandb/components';
import {Link, TargetBlank} from '@wandb/weave/common/util/links';
import React, {useMemo, useState} from 'react';
import {useHistory} from 'react-router';
import {Popup} from 'semantic-ui-react';

import {
  useDeleteRoleMutation,
  useOrgAdminsQuery,
} from '../../../generated/graphql';
import {useViewer} from '../../../state/viewer/hooks';
import {
  contactSalesPricing,
  orgRoleEdit,
  orgRolesNew,
} from '../../../util/urls';
import {SettingsCard} from '../../Billing/AccountSettings/SettingsTab/SettingsCard';
import {RoleDescription, RoleItem, RoleName} from './CommonRoles';
import {DeleteCustomRoleModal} from './DeleteCustomRoleModal';
import {useOrgRolesContext} from './OrgRolesContext';
import {useIsRolesDashboardEnabled} from './useIsRolesDashboardEnabled';
import {type CustomRole} from './useOrgRolesContextResult';

const RoleOptions: React.FC<CustomRole & {viewerIsOrgAdmin: boolean}> = ({
  id,
  name,
  inheritedFrom,
  viewerIsOrgAdmin,
}) => {
  const history = useHistory();
  const {data, refetch} = useOrgRolesContext();
  const [isOpen, setIsOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [deleteRoleMutation] = useDeleteRoleMutation();

  return (
    <>
      <DropdownMenu.Trigger isOpen={isOpen} onOpenChange={setIsOpen}>
        {viewerIsOrgAdmin ? (
          <>
            <IconButton
              icon="overflow-horizontal"
              variant="ghost"
              aria-label="Custom role actions"
              size="sm"
            />
          </>
        ) : null}
        <DropdownMenu.Content>
          <DropdownMenu.Item
            onAction={() => {
              history.push(orgRoleEdit(data.orgName, id));
            }}>
            <DropdownMenu.ItemLabel>Edit role</DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
          <DropdownMenu.Item
            onAction={() => {
              setIsModalOpen(true);
            }}>
            <DropdownMenu.ItemLabel>Delete role</DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Trigger>
      <DeleteCustomRoleModal
        isOpen={isModalOpen}
        close={() => setIsModalOpen(false)}
        name={name}
        inheritedFrom={inheritedFrom}
        onDelete={async () => {
          await deleteRoleMutation({
            variables: {
              roleID: id,
            },
          });
          refetch();
        }}
      />
    </>
  );
};

const CustomRoleItem: React.FC<
  CustomRole & {viewerIsOrgAdmin: boolean}
> = role => {
  const {name, description} = role;
  return (
    <RoleItem>
      <div className="flex-grow text-left">
        <RoleName>{name}</RoleName>
        <RoleDescription>{description}</RoleDescription>
      </div>
      <RoleOptions {...role} />
    </RoleItem>
  );
};

type CustomRolesProps = {
  orgName: string;
  roles: CustomRole[];
};

const linkStyle =
  'text-wb-accent-text font-semibold hover:text-wb-text-link-hover';

export const CustomRoles: React.FC<CustomRolesProps> = ({orgName, roles}) => {
  const history = useHistory();
  const viewer = useViewer();
  const {data: orgAdminsData} = useOrgAdminsQuery({
    variables: {orgName},
    skip: orgName === '',
  });
  const viewerIsOrgAdmin = useMemo(
    () =>
      orgAdminsData?.organization?.members.find(
        m => m.user.username === viewer?.username
      )?.admin === true,
    [orgAdminsData?.organization?.members, viewer?.username]
  );
  const isRolesDashboardEnabled = useIsRolesDashboardEnabled();

  const hasNoRoles = !isRolesDashboardEnabled || roles.length === 0;
  let noRolesContent = null;
  if (!isRolesDashboardEnabled) {
    noRolesContent = (
      <>
        <TargetBlank href={contactSalesPricing()} className={linkStyle}>
          Contact sales
        </TargetBlank>{' '}
        to upgrade and access this feature.
      </>
    );
  }
  if (isRolesDashboardEnabled && roles.length === 0) {
    noRolesContent = viewerIsOrgAdmin ? (
      <>
        <Link to={orgRolesNew(orgName)} className={linkStyle}>
          Create a custom role
        </Link>{' '}
        to get started.
      </>
    ) : (
      'No custom roles configured.'
    );
  }

  return (
    <SettingsCard
      title="Custom roles"
      description="Create a role by inheriting a pre-defined role and adding
            permissions to it."
      isPremiumFeatureDisabled={!isRolesDashboardEnabled}
      sideContent={
        !isRolesDashboardEnabled || !viewerIsOrgAdmin ? (
          <Popup
            inverted
            size="mini"
            position="top right"
            trigger={
              <span>
                <Button
                  startIcon="add-new"
                  size="md"
                  variant="secondary"
                  isDisabled
                  onPress={() => {}}>
                  Create a role
                </Button>
              </span>
            }
            content={
              !isRolesDashboardEnabled
                ? 'Contact sales to upgrade and access custom roles.'
                : 'Only organization admins can create custom roles.'
            }
          />
        ) : (
          <Button
            startIcon="add-new"
            size="md"
            variant="secondary"
            onPress={() => history.push(orgRolesNew(orgName))}>
            Create a role
          </Button>
        )
      }>
      {hasNoRoles ? (
        <div className="flex justify-center text-wb-text-tertiary">
          <div>{noRolesContent}</div>
        </div>
      ) : (
        roles.map(r => (
          <div
            key={r.id}
            className="items-center border-b border-wb-border py-12 first:pt-0 last:border-transparent last:pb-0">
            <CustomRoleItem {...r} viewerIsOrgAdmin={viewerIsOrgAdmin} />
          </div>
        ))
      )}
    </SettingsCard>
  );
};
